import { createSlice } from "@reduxjs/toolkit"
import axios from "axios"

const initialState = {
    // Add to cart
    addToCartLoading: false,
    addToCartSuccess: false,
    addToCartFail: false,
    addToCartData: null,
    addToCartErrorMessage: null,

    // Get cart
    getCartLoading: false,
    getCartSuccess: false,
    getCartFail: false,
    getCartData: null, // { data: [...items], summary: { total_items, total_credit } }
    getCartErrorMessage: null,

    // Update cart item
    updateCartItemLoading: false,
    updateCartItemSuccess: false,
    updateCartItemFail: false,
    updateCartItemData: null,
    updateCartItemErrorMessage: null,

    // Remove from cart
    removeFromCartLoading: false,
    removeFromCartSuccess: false,
    removeFromCartFail: false,
    removeFromCartData: null,
    removeFromCartErrorMessage: null,

    // Which cart rows are ticked for checkout. Lives here rather than in the cart
    // page's local state because /checkout re-fetches the cart on mount and would
    // otherwise have no idea what the customer picked. Null means "not chosen yet",
    // which the cart page reads as select-all.
    selectedCartItemIds: null,
}

export const cartSlice = createSlice({
    name: "cart",
    initialState,
    reducers: {
        // Add to Cart Actions
        addToCartRequest: (state) => {
            state.addToCartLoading = true
            state.addToCartSuccess = false
            state.addToCartFail = false
            state.addToCartErrorMessage = null
        },
        addToCartSuccessAction: (state, action) => {
            state.addToCartLoading = false
            state.addToCartSuccess = true
            state.addToCartFail = false
            state.addToCartData = action.payload
            state.addToCartErrorMessage = null
        },
        addToCartFailAction: (state, action) => {
            state.addToCartLoading = false
            state.addToCartSuccess = false
            state.addToCartFail = true
            state.addToCartErrorMessage = action.payload
        },
        resetAddToCart: (state) => {
            state.addToCartLoading = false
            state.addToCartSuccess = false
            state.addToCartFail = false
            state.addToCartData = null
            state.addToCartErrorMessage = null
        },

        // Get Cart Actions
        getCartRequest: (state) => {
            state.getCartLoading = true
            state.getCartSuccess = false
            state.getCartFail = false
            state.getCartErrorMessage = null
        },
        getCartSuccessAction: (state, action) => {
            state.getCartLoading = false
            state.getCartSuccess = true
            state.getCartFail = false
            state.getCartData = action.payload
            state.getCartErrorMessage = null
        },
        getCartFailAction: (state, action) => {
            state.getCartLoading = false
            state.getCartSuccess = false
            state.getCartFail = true
            state.getCartErrorMessage = action.payload
        },
        resetGetCart: (state) => {
            state.getCartLoading = false
            state.getCartSuccess = false
            state.getCartFail = false
            state.getCartData = null
            state.getCartErrorMessage = null
        },

        // Update Cart Item Actions
        updateCartItemRequest: (state) => {
            state.updateCartItemLoading = true
            state.updateCartItemSuccess = false
            state.updateCartItemFail = false
            state.updateCartItemErrorMessage = null
        },
        updateCartItemSuccessAction: (state, action) => {
            state.updateCartItemLoading = false
            state.updateCartItemSuccess = true
            state.updateCartItemFail = false
            state.updateCartItemData = action.payload
            state.updateCartItemErrorMessage = null
        },
        updateCartItemFailAction: (state, action) => {
            state.updateCartItemLoading = false
            state.updateCartItemSuccess = false
            state.updateCartItemFail = true
            state.updateCartItemErrorMessage = action.payload
        },
        resetUpdateCartItem: (state) => {
            state.updateCartItemLoading = false
            state.updateCartItemSuccess = false
            state.updateCartItemFail = false
            state.updateCartItemData = null
            state.updateCartItemErrorMessage = null
        },

        // Remove from Cart Actions
        removeFromCartRequest: (state) => {
            state.removeFromCartLoading = true
            state.removeFromCartSuccess = false
            state.removeFromCartFail = false
            state.removeFromCartErrorMessage = null
        },
        removeFromCartSuccessAction: (state, action) => {
            state.removeFromCartLoading = false
            state.removeFromCartSuccess = true
            state.removeFromCartFail = false
            state.removeFromCartData = action.payload
            state.removeFromCartErrorMessage = null
        },
        removeFromCartFailAction: (state, action) => {
            state.removeFromCartLoading = false
            state.removeFromCartSuccess = false
            state.removeFromCartFail = true
            state.removeFromCartErrorMessage = action.payload
        },
        resetRemoveFromCart: (state) => {
            state.removeFromCartLoading = false
            state.removeFromCartSuccess = false
            state.removeFromCartFail = false
            state.removeFromCartData = null
            state.removeFromCartErrorMessage = null
        },
        setSelectedCartItemIds: (state, action) => {
            state.selectedCartItemIds = action.payload
        },
        resetSelectedCartItemIds: (state) => {
            state.selectedCartItemIds = null
        },
    },
})

// Action creators
export const {
    addToCartRequest,
    addToCartSuccessAction,
    addToCartFailAction,
    resetAddToCart,
    getCartRequest,
    getCartSuccessAction,
    getCartFailAction,
    resetGetCart,
    updateCartItemRequest,
    updateCartItemSuccessAction,
    updateCartItemFailAction,
    resetUpdateCartItem,
    removeFromCartRequest,
    removeFromCartSuccessAction,
    removeFromCartFailAction,
    resetRemoveFromCart,
    setSelectedCartItemIds,
    resetSelectedCartItemIds,
} = cartSlice.actions

// Selectors
export const getAddToCartLoading = (state) => state.cart.addToCartLoading
export const getAddToCartSuccess = (state) => state.cart.addToCartSuccess
export const getAddToCartFail = (state) => state.cart.addToCartFail
export const getAddToCartData = (state) => state.cart.addToCartData
export const getAddToCartErrorMessage = (state) => state.cart.addToCartErrorMessage

export const getCartLoading = (state) => state.cart.getCartLoading
export const getCartSuccess = (state) => state.cart.getCartSuccess
export const getCartFail = (state) => state.cart.getCartFail
export const getCartData = (state) => state.cart.getCartData
export const getCartErrorMessage = (state) => state.cart.getCartErrorMessage

export const getUpdateCartItemLoading = (state) => state.cart.updateCartItemLoading
export const getUpdateCartItemSuccess = (state) => state.cart.updateCartItemSuccess
export const getUpdateCartItemFail = (state) => state.cart.updateCartItemFail
export const getUpdateCartItemData = (state) => state.cart.updateCartItemData
export const getUpdateCartItemErrorMessage = (state) => state.cart.updateCartItemErrorMessage

export const getRemoveFromCartLoading = (state) => state.cart.removeFromCartLoading
export const getRemoveFromCartSuccess = (state) => state.cart.removeFromCartSuccess
export const getRemoveFromCartFail = (state) => state.cart.removeFromCartFail
export const getRemoveFromCartData = (state) => state.cart.removeFromCartData
export const getRemoveFromCartErrorMessage = (state) => state.cart.removeFromCartErrorMessage

export const getSelectedCartItemIds = (state) => state.cart.selectedCartItemIds

// Thunks (API calls)

/**
 * Add product to cart
 * @param {object} cartData - { product_id, quantity, product_variant_id? }
 */
export const doAddToCart = (cartData) => {
    return async (dispatch) => {
        dispatch(addToCartRequest())

        try {
            // Normalize — pass product_variant_id through when provided.
            const payload = {
                product_id: cartData.product_id,
                quantity: cartData.quantity,
            };
            if (cartData.product_variant_id) {
                payload.product_variant_id = cartData.product_variant_id;
            }
            if (Array.isArray(cartData.bundle_selections)) {
                payload.bundle_selections = cartData.bundle_selections;
            }

            const response = await axios.post(`/api/customer/cart`, payload);

            dispatch(addToCartSuccessAction(response.data.data))
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Network error occurred"
            dispatch(addToCartFailAction(errorMessage))
        }
    }
}

/**
 * Get user's cart
 */
export const doGetCart = () => {
    return async (dispatch) => {
        dispatch(getCartRequest());

        try {
            const response = await axios.get(`/api/customer/cart`);

            // if (response.data.success) {
                dispatch(getCartSuccessAction(response.data));
            // } else {
            //     dispatch(getCartFailAction(response.data.message || "Failed to fetch cart"))
            // }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Network error occurred";
            dispatch(getCartFailAction(errorMessage));
        }
    }
}

/**
 * Update cart item quantity
 * @param {object} updateData - { cart_item_id, quantity, bundle_selections? }
 *   bundle_selections is REQUIRED for bundle lines: the picks are absolute, so a new
 *   quantity means a new required total (bundle_size × quantity), and the server
 *   replaces the stored set with whatever is sent here.
 */
export const doUpdateCartItem = (updateData) => {
    return async (dispatch) => {
        dispatch(updateCartItemRequest())

        try {
            const payload = {
                cart_item_id: updateData.cart_item_id,
                quantity: updateData.quantity,
            }
            if (Array.isArray(updateData.bundle_selections)) {
                payload.bundle_selections = updateData.bundle_selections
            }
            const response = await axios.put(
                `/api/customer/cart/${updateData.cart_item_id}`,
                payload
            )

            // if (response.data.success) {
                dispatch(updateCartItemSuccessAction(response.data.data))
            // } else {
            //     dispatch(updateCartItemFailAction(response.data.message || "Failed to update cart item"))
            // }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Network error occurred"
            dispatch(updateCartItemFailAction(errorMessage))
        }
    }
}

/**
 * Remove item from cart
 * @param {string} cartItemId - The cart item ID
 */
export const doRemoveFromCart = (cartItemId) => {
    return async (dispatch) => {
        dispatch(removeFromCartRequest())

        try {
            const response = await axios.delete(
                `/api/customer/cart/${cartItemId}`
            )

            // if (response.data.success) {
                dispatch(removeFromCartSuccessAction(response.data.data))
            // } else {
            //     dispatch(removeFromCartFailAction(response.data.message || "Failed to remove from cart"))
            // }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Network error occurred"
            dispatch(removeFromCartFailAction(errorMessage))
        }
    }
}

export default cartSlice.reducer
